import React, { useState, useEffect } from 'react';
import styles from './Components/CSS/AudioSeries.module.css';
import { useNavigate } from 'react-router-dom';
import Loading from '../common/HousGlass Loader/Loading';

const AudioSeries = ({ series = [], loading }) => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchPosition, setTouchPosition] = useState(null);
  const minSwipeDistance = 50; // Minimum swipe distance in pixels

  // Reset currentSlide when series data changes
  useEffect(() => {
    setCurrentSlide(0);
  }, [series]);

  if (loading) {
    return (
      <div className={styles.mainContainer}>
        <div className={styles.contentBox}>
          <div className={styles.loadingContainer}><Loading/></div>
        </div>
      </div>
    );
  }

  if (!series || series.length === 0) {
    return (
      <div className={styles.mainContainer}>
        <div className={styles.contentBox}>
          <div className={styles.emptyContainer}>No audio series available.</div>
        </div>
      </div>
    );
  }

  const handleTouchStart = (e) => {
    const touchDown = e.touches[0].clientX;
    setTouchPosition(touchDown);
  };

  const handleTouchMove = (e) => {
    if (touchPosition === null) return;
    const currentTouch = e.touches[0].clientX;
    const diff = touchPosition - currentTouch;
    if (diff > minSwipeDistance) {
      // Swipe left (next slide)
      handleNext();
      setTouchPosition(null);
    } else if (diff < -minSwipeDistance) {
      // Swipe right (previous slide)
      handlePrev();
      setTouchPosition(null);
    }
  };

  const handleTouchEnd = () => {
    setTouchPosition(null);
  };

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % series.length);
  };

  const handlePrev = () => {
    setCurrentSlide((prev) => (prev - 1 + series.length) % series.length);
  };

  const handleIndicatorClick = (index) => {
    setCurrentSlide(index);
  };

  const handleListenClick = (slug) => {
    navigate(`/audio-play/${slug}`);
  };

  return (
    <div className={styles.mainContainer}>
      {/* Reserved space for menu */}
      <div className={styles.topMenu}>
        {/* Add your menu here */}
      </div>

      {/* Boxed content container */}
      <div className={styles.contentBox}>
        <div className={styles.audioSeriesContainer}>
          <div
            className={styles.sliderWrapper}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className={styles.slider}>
              {series.map((item, index) => (
                <div
                  key={item.id}
                  className={`${styles.slide} ${currentSlide === index ? styles.active : ''}`}
                >
                  <div className={styles.cardWrapper}>
                    <div className={styles.imageContainer}>
                      <div
                        className={styles.backgroundImage}
                        style={{ backgroundImage: `url(${item.coverImageUrl})` }}
                      />
                      <div className={styles.overlay} />
                    </div>

                    <div className={styles.category}>
                      {item.category?.name || item.seriesType || 'Audio'}
                      {item.isPremium && <span className={styles.premium}> • Premium</span>}
                    </div>

                    <div className={styles.content}>
                      <div className={styles.textContent}>
                        <h1 className={styles.title}>{item.title}</h1>
                        <p className={styles.description}>{item.description}</p>
                        <div className={styles.metadata}>
                          <span>By {item.creator?.name || 'Unknown Creator'}</span>
                        </div>
                      </div>
                      <button 
                        className={styles.listenButton}
                        onClick={() => handleListenClick(item.slug)}
                      >
                        <span className={styles.playIcon}>▶</span> Listen Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Buttons */}
            {series.length > 1 && (
              <>
                <button className={`${styles.navButton} ${styles.prevButton}`} onClick={handlePrev}>
                  &#10094;
                </button>
                <button className={`${styles.navButton} ${styles.nextButton}`} onClick={handleNext}>
                  &#10095;
                </button>

                {/* Slider indicators */}
                <div className={styles.sliderControls}>
                  {series.map((_, index) => (
                    <button
                      key={index}
                      className={`${styles.indicator} ${
                        currentSlide === index ? styles.activeIndicator : ''
                      }`}
                      onClick={() => handleIndicatorClick(index)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioSeries;